import { signal, useSignalValue } from "../../../utils/signal";
import {
  MyBetFingerprint,
  TrackBetPayload,
  fetchMyBetsFingerprints,
} from "./myBetsApi";

/**
 * Client store for "which bets has this user already tracked" — powers
 * the boards' already-bet badges. Server-backed (hydrated from
 * GET /my-bets/fingerprints on first use, so it survives reloads and
 * other devices) with optimistic updates on track/untrack.
 *
 * Matching rule (F2): a board row is "tracked" when ANY of its candidate
 * keys intersects the tracked-key set. Every key is built by the same
 * normalizer on both sides:
 *   k1 = oddId|marketType|selection|betType   (raw identity, arb/sharp)
 *   k2 = oddId|"selection betType"            (combined cell text, +EV)
 * Boards MUST build their payload and their badge keys from the same
 * candidate object so the two can never drift.
 */

interface TrackedBetsState {
  loaded: boolean;
  keys: Set<string>;
  /** Open-bet count drives the free-tier cap messaging. */
  pendingCount: number;
}

const EMPTY_STATE: TrackedBetsState = { loaded: false, keys: new Set(), pendingCount: 0 };

export const trackedBetsSignal = signal<TrackedBetsState>(EMPTY_STATE);

/** Collapse whitespace, lowercase — mirror of the backend normalizer. */
const norm = (value: string | null | undefined): string =>
  (value ?? "").trim().toLowerCase().replace(/\s+/g, " ");

/** Candidate keys for one bet reference; empty strings never match. */
export function fingerprintKeys(parts: {
  oddId?: string | null;
  marketType?: string | null;
  selection?: string | null;
  betType?: string | null;
  selectionName?: string | null;
}): string[] {
  const oddId = norm(parts.oddId);
  if (!oddId) return [];
  const keys: string[] = [];
  const marketType = norm(parts.marketType);
  const selection = norm(parts.selection);
  const betType = norm(parts.betType);
  if (marketType && (selection || betType)) {
    keys.push(`${oddId}|${marketType}|${selection}|${betType}`);
  }
  const combined = norm(parts.selectionName) || norm(`${parts.selection ?? ""} ${parts.betType ?? ""}`);
  if (combined) {
    keys.push(`${oddId}~${combined}`);
  }
  return keys;
}

/** Keys for a payload a board is about to send (or just sent). */
export function payloadFingerprintKeys(payload: TrackBetPayload): string[] {
  return fingerprintKeys({
    oddId: payload.odd_id,
    marketType: payload.market_key,
    selection: payload.selection,
    betType: payload.bet_type,
    selectionName: payload.selection_name,
  });
}

function serverRowKeys(row: MyBetFingerprint): string[] {
  return fingerprintKeys({
    oddId: row.oddId,
    marketType: row.marketType,
    selection: row.selection,
    betType: row.betType,
    selectionName:
      row.selection || row.betType ? `${row.selection ?? ""} ${row.betType ?? ""}` : null,
  });
}

let hydrating: Promise<void> | null = null;

/**
 * Idempotent server hydration. Call from any board/page that shows
 * badges; signed-out sessions resolve to an empty loaded state.
 */
export function ensureTrackedBetsLoaded(force = false): Promise<void> {
  if (!localStorage.getItem("access_token")) {
    trackedBetsSignal.value = { ...EMPTY_STATE, loaded: true };
    return Promise.resolve();
  }
  if (!force && (trackedBetsSignal.peek().loaded || hydrating)) {
    return hydrating ?? Promise.resolve();
  }
  hydrating = (async () => {
    const response = await fetchMyBetsFingerprints();
    const rows = response?.bets ?? [];
    const keys = new Set<string>();
    rows.forEach((row) => serverRowKeys(row).forEach((key) => keys.add(key)));
    trackedBetsSignal.value = {
      loaded: true,
      keys,
      // Parlay leg rows are badge-only expansions of one bet — counting
      // them would overstate the open-bet cap usage.
      pendingCount: rows.filter((row) => !row.settled && !row.leg).length,
    };
  })().finally(() => {
    hydrating = null;
  });
  return hydrating;
}

/** Optimistic add right after a successful track. */
export function noteTracked(payload: TrackBetPayload): void {
  const state = trackedBetsSignal.peek();
  const keys = new Set(state.keys);
  payloadFingerprintKeys(payload).forEach((key) => keys.add(key));
  trackedBetsSignal.value = {
    loaded: true,
    keys,
    pendingCount: state.pendingCount + 1,
  };
  // Reconcile with the server's resolved identity in the background.
  void ensureTrackedBetsLoaded(true);
}

/**
 * Optimistic add for a just-tracked parlay: one open bet, but a badge
 * key per leg so every leg's board row flips to BET immediately (the
 * server echoes the same keys from legs_json on the next hydration).
 */
export function noteTrackedParlay(
  legs: Array<{ oddId?: string | null; selectionName: string }>
): void {
  const state = trackedBetsSignal.peek();
  const keys = new Set(state.keys);
  legs.forEach((leg) =>
    fingerprintKeys({ oddId: leg.oddId, selectionName: leg.selectionName }).forEach((key) =>
      keys.add(key)
    )
  );
  trackedBetsSignal.value = {
    loaded: true,
    keys,
    pendingCount: state.pendingCount + 1,
  };
  void ensureTrackedBetsLoaded(true);
}

/** After delete/settle the set may shrink; just re-hydrate. */
export function refreshTrackedBets(): Promise<void> {
  return ensureTrackedBetsLoaded(true);
}

export function isTracked(candidateKeys: string[]): boolean {
  if (candidateKeys.length === 0) return false;
  const { keys } = trackedBetsSignal.peek();
  return candidateKeys.some((key) => keys.has(key));
}

/** Reactive variant for row rendering. */
export function useIsTracked(candidateKeys: string[]): boolean {
  const state = useSignalValue(trackedBetsSignal);
  if (candidateKeys.length === 0) return false;
  return candidateKeys.some((key) => state.keys.has(key));
}
